import { useEffect, useRef, useState } from 'react';
import muMeteor1 from '../../levels/mu/obstacles/m_1.png';
import muMeteor2 from '../../levels/mu/obstacles/m_2.png';
import muMeteor3 from '../../levels/mu/obstacles/m_3.png';
import muMeteor4 from '../../levels/mu/obstacles/m_4.png';
import {
  MU_OVERLAY_FLIGHT_MS,
  MU_OVERLAY_HOLD_MS,
} from './gameplayConstants';
import { MU_OVERLAY_PATHS } from './muOverlayTargets';
import './LevelMuOverlay.css';

const SRC: Record<(typeof MU_OVERLAY_PATHS)[number]['srcKey'], string> = {
  m_1: muMeteor1,
  m_2: muMeteor2,
  m_3: muMeteor3,
  m_4: muMeteor4,
};

type ActiveMeteor = {
  id: number;
  pathIndex: number;
  elapsedMs: number;
};

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

type LevelMuOverlayProps = {
  isPaused: boolean;
  onComplete: () => void;
};

export function LevelMuOverlay({
  isPaused,
  onComplete,
}: LevelMuOverlayProps) {
  const [meteors, setMeteors] = useState<ActiveMeteor[]>([]);
  const meteorsRef = useRef<ActiveMeteor[]>([]);
  const windowMsRef = useRef(MU_OVERLAY_HOLD_MS);
  const nextIdRef = useRef(1);
  const spawnedOnceRef = useRef(false);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let frameId = 0;
    let lastNow = performance.now();

    const tick = (now: number) => {
      const dt = now - lastNow;
      lastNow = now;
      windowMsRef.current = Math.max(0, windowMsRef.current - dt);

      if (!spawnedOnceRef.current) {
        spawnedOnceRef.current = true;
        meteorsRef.current = MU_OVERLAY_PATHS.map((_, pathIndex) => ({
          id: nextIdRef.current++,
          pathIndex,
          elapsedMs: 0,
        }));
      }

      const next: ActiveMeteor[] = [];
      for (const meteor of meteorsRef.current) {
        const elapsed = meteor.elapsedMs + dt;
        if (elapsed < MU_OVERLAY_FLIGHT_MS) {
          next.push({ ...meteor, elapsedMs: elapsed });
        }
      }
      meteorsRef.current = next;
      setMeteors(next);

      if (
        windowMsRef.current <= 0 &&
        next.length === 0 &&
        !completedRef.current
      ) {
        completedRef.current = true;
        onCompleteRef.current();
        return;
      }

      // Re-launch waves while the hold window is open.
      if (windowMsRef.current > 0 && next.length === 0) {
        meteorsRef.current = MU_OVERLAY_PATHS.map((_, pathIndex) => ({
          id: nextIdRef.current++,
          pathIndex,
          elapsedMs: 0,
        }));
        setMeteors(meteorsRef.current);
      }

      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isPaused]);

  return (
    <div className="level-mu-overlay" aria-hidden>
      {meteors.map((meteor) => {
        const path = MU_OVERLAY_PATHS[meteor.pathIndex];
        const t = easeInOut(
          Math.min(1, Math.max(0, meteor.elapsedMs / MU_OVERLAY_FLIGHT_MS)),
        );
        const xPct =
          path.startXPct + (path.finishXPct - path.startXPct) * t;
        const yPct =
          path.startYPct + (path.finishYPct - path.startYPct) * t;
        return (
          <img
            key={meteor.id}
            className="level-mu-overlay__meteor"
            src={SRC[path.srcKey]}
            alt=""
            style={{
              left: `${xPct}%`,
              top: `${yPct}%`,
              width: `${path.widthPct}%`,
            }}
          />
        );
      })}
    </div>
  );
}
